import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Move, RotateCcw, Save, Filter, Plus, Edit2, Trash2, 
  Users, Clock, DollarSign, AlertTriangle, Lock, Circle, Square,
  CalendarClock, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import TableActionDrawer from '../../components/TableActionDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors - tablet optimized with high contrast
const STATUS_CONFIG = {
  available: { 
    bg: 'bg-muted', 
    border: 'border-border', 
    text: 'text-foreground',
    label: 'Available',
    hoverBorder: 'hover:border-secondary'
  },
  seated: { 
    bg: 'bg-blue-900/70', 
    border: 'border-blue-400', 
    text: 'text-blue-100',
    label: 'Seated',
    hoverBorder: 'hover:border-blue-300'
  },
  ordering: { 
    bg: 'bg-yellow-900/70', 
    border: 'border-yellow-400', 
    text: 'text-yellow-100',
    label: 'Ordering',
    hoverBorder: 'hover:border-yellow-300'
  },
  pending_payment: { 
    bg: 'bg-orange-900/70', 
    border: 'border-orange-400', 
    text: 'text-orange-100',
    label: 'Pending Payment',
    hoverBorder: 'hover:border-orange-300'
  },
  reserved: { 
    bg: 'bg-purple-900/70', 
    border: 'border-purple-400', 
    text: 'text-purple-100',
    label: 'Reserved',
    hoverBorder: 'hover:border-purple-300'
  },
  cleaning: { 
    bg: 'bg-red-900/70', 
    border: 'border-red-400', 
    text: 'text-red-100',
    label: 'Cleaning',
    hoverBorder: 'hover:border-red-300'
  }
};

// Default section
const DEFAULT_SECTION = 'Main';

export default function TableMapPage({ outletId, onTableSelect, dayLocked = false }) {
  const { token, user } = useAuth();
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Drag state
  const [draggedTable, setDraggedTable] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  // Dialogs
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  
  // Table Action Drawer
  const [selectedTable, setSelectedTable] = useState(null);
  const [showActionDrawer, setShowActionDrawer] = useState(false);
  
  // Permission check - user.role_name from AuthContext
  const canEditLayout = user?.role_name === 'owner' || user?.role_name === 'manager' || user?.role_name === 'store_manager';

  const fetchTables = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tables?outlet_id=${outletId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTables(data);
      
      // Extract unique sections
      const secs = [...new Set(data.map(t => t.section).filter(Boolean))];
      setSections(secs.length > 0 ? secs : [DEFAULT_SECTION]);
    } catch (err) {
      console.error('Failed to load tables:', err);
      toast.error('Failed to load tables');
    }
    setLoading(false);
  }, [outletId, token]);

  useEffect(() => {
    fetchTables();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  const filteredTables = selectedSection === 'all' 
    ? tables 
    : tables.filter(t => t.section === selectedSection);

  // Summary stats
  const stats = {
    total: tables.length,
    available: tables.filter(t => t.status === 'available').length,
    occupied: tables.filter(t => t.status !== 'available').length,
    totalGuests: tables.reduce((sum, t) => sum + (t.ticket_summary?.guest_count || 0), 0)
  };

  // Drag handlers - Touch and Mouse
  const handleDragStart = (e, table) => {
    if (!editMode || !canEditLayout) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDraggedTable(table);
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
  };

  const handleDrag = useCallback((e) => {
    if (!draggedTable || !editMode || !containerRef.current) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - containerRect.left - dragOffset.x, containerRect.width - (draggedTable.width || 80)));
    const y = Math.max(0, Math.min(clientY - containerRect.top - dragOffset.y, containerRect.height - (draggedTable.height || 80)));
    
    // Snap to grid (20px)
    const snappedX = Math.round(x / 20) * 20;
    const snappedY = Math.round(y / 20) * 20;
    
    setTables(prev => prev.map(t => 
      t.id === draggedTable.id 
        ? { ...t, position_x: snappedX, position_y: snappedY } 
        : t
    ));
    
    // Track pending changes
    setPendingChanges(prev => {
      const existing = prev.find(p => p.id === draggedTable.id);
      if (existing) {
        return prev.map(p => p.id === draggedTable.id 
          ? { ...p, position_x: snappedX, position_y: snappedY }
          : p
        );
      }
      return [...prev, { id: draggedTable.id, position_x: snappedX, position_y: snappedY }];
    });
  }, [draggedTable, editMode, dragOffset]);

  const handleDragEnd = () => {
    setDraggedTable(null);
  };

  useEffect(() => {
    if (editMode) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDrag, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDrag);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [editMode, handleDrag]);

  const saveLayout = async () => {
    if (pendingChanges.length === 0) {
      setEditMode(false);
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/tables/layout/bulk?outlet_id=${outletId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ tables: pendingChanges })
      });
      toast.success('Layout saved');
      setPendingChanges([]);
      setEditMode(false);
    } catch (err) {
      toast.error('Failed to save layout');
    }
  };

  const cancelEdit = () => {
    setPendingChanges([]);
    setEditMode(false);
    fetchTables();
  };

  const handleTableClick = (table) => {
    if (editMode) {
      setEditingTable(table);
      return;
    }
    
    // Open the action drawer instead of directly calling onTableSelect
    setSelectedTable(table);
    setShowActionDrawer(true);
  };
  
  // Handler for when user selects "Seat Guests" from the drawer
  const handleSeatFromDrawer = (table) => {
    if (dayLocked && table.display_status === 'available') {
      toast.error('Day is locked. Cannot seat new guests.');
      return;
    }
    // Call the parent's onTableSelect which will handle the guest modal
    onTableSelect(table);
  };
  
  // Handler for when user selects "Resume Order" from the drawer
  const handleResumeFromDrawer = (table) => {
    // Same as selecting - parent handles loading the ticket
    onTableSelect(table);
  };

  const handleCreateTable = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/tables?outlet_id=${outletId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(data)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create table');
      }
      
      toast.success(`Table ${data.name} created`);
      setShowCreateTable(false);
      fetchTables();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateTable = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/tables/${editingTable.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(data)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update table');
      }
      
      toast.success(`Table ${data.name || editingTable.name} updated`);
      setEditingTable(null);
      fetchTables();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!confirm('Are you sure you want to delete this table?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tables/${tableId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete table');
      }
      
      toast.success('Table deleted');
      setEditingTable(null);
      fetchTables();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatElapsed = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-background min-h-0" data-testid="table-map-page">
      {/* Left Sidebar - Section Filter */}
      <aside className="w-52 bg-card border-r border-border flex flex-col">
        {/* Stats Summary */}
        <div className="p-4 border-b border-border">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Summary</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-secondary font-bold">{stats.available}</div>
              <div className="text-muted-foreground text-xs">Available</div>
            </div>
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-orange-600 font-bold">{stats.occupied}</div>
              <div className="text-muted-foreground text-xs">Occupied</div>
            </div>
            <div className="bg-muted rounded p-2 text-center col-span-2">
              <div className="text-blue-600 font-bold flex items-center justify-center gap-1">
                <Users className="w-3 h-3" /> {stats.totalGuests}
              </div>
              <div className="text-muted-foreground text-xs">Total Guests</div>
            </div>
          </div>
        </div>
        
        {/* Section Filter */}
        <div className="p-4 border-b border-border">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" /> Sections
          </h3>
          <ScrollArea className="max-h-64">
            <div className="space-y-1">
              <button 
                onClick={() => setSelectedSection('all')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedSection === 'all' 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-muted'
                }`}
                data-testid="section-all"
              >
                All Sections ({tables.length})
              </button>
              {sections.map(section => {
                const count = tables.filter(t => t.section === section).length;
                return (
                  <button 
                    key={section}
                    onClick={() => setSelectedSection(section)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedSection === section 
                        ? 'bg-secondary text-foreground' 
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`section-${section.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {section} ({count})
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
        
        {/* Status Legend */}
        <div className="p-4 flex-1">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Status Legend</h3>
          <div className="space-y-2">
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <div key={key} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${config.bg} ${config.border} border`} />
                <span className="text-xs text-muted-foreground">{config.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Day Locked Warning */}
        {dayLocked && (
          <div className="p-4 border-t border-border">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-red-600" />
              <span className="text-xs text-red-300">Day Locked</span>
            </div>
          </div>
        )}
      </aside>
      
      {/* Main Map Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="h-14 bg-card border-b border-border flex items-center px-4 gap-4">
          <h2 className="text-lg font-semibold text-foreground">Table Map</h2>
          
          <div className="flex-1" />
          
          {editMode && pendingChanges.length > 0 && (
            <span className="text-sm text-yellow-600">
              {pendingChanges.length} unsaved change{pendingChanges.length > 1 ? 's' : ''}
            </span>
          )}
          
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowCreateTable(true)}
                  className="h-11 px-4"
                  data-testid="add-table-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Table
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={cancelEdit}
                  className="h-11 px-4"
                >
                  <RotateCcw className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={saveLayout} 
                  className="bg-secondary hover:bg-secondary/90 h-11 px-4"
                  data-testid="save-layout-btn"
                >
                  <Save className="w-4 h-4 mr-1" /> Save Layout
                </Button>
              </>
            ) : (
              canEditLayout && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setEditMode(true)} 
                  className="h-11 px-4"
                  data-testid="edit-layout-btn"
                >
                  <Move className="w-4 h-4 mr-1" /> Edit Layout
                </Button>
              )
            )}
          </div>
        </div>
        
        {/* Map Container */}
        <div 
          ref={containerRef}
          className="flex-1 relative overflow-auto p-4"
          data-testid="table-map-container"
        >
          <div className="relative min-w-[1200px] min-h-[600px] bg-card/30 rounded-lg">
            {/* Grid background for edit mode */}
            {editMode && (
              <div 
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: 'linear-gradient(to right, #374151 1px, transparent 1px), linear-gradient(to bottom, #374151 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
            )}
            
            {filteredTables.map(table => {
              const displayStatus = table.display_status || table.status;
              const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.available;
              const isCircle = table.shape === 'circle';
              const isDragging = draggedTable?.id === table.id;
              
              return (
                <div
                  key={table.id}
                  data-testid={`table-${table.name}`}
                  className={`absolute flex flex-col items-center justify-center cursor-pointer 
                    transition-all duration-150 border-2
                    ${isCircle ? 'rounded-full' : 'rounded-lg'}
                    ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}
                    ${editMode ? 'cursor-move' : statusConfig.hoverBorder}
                    ${isDragging ? 'opacity-70 scale-105 z-50' : 'z-10'}
                    ${editMode && pendingChanges.find(p => p.id === table.id) ? 'ring-2 ring-yellow-400' : ''}
                  `}
                  style={{
                    left: table.position_x,
                    top: table.position_y,
                    width: table.width || 80,
                    height: table.height || 80,
                    minWidth: 60,
                    minHeight: 60,
                    touchAction: 'none'
                  }}
                  onMouseDown={(e) => handleDragStart(e, table)}
                  onTouchStart={(e) => handleDragStart(e, table)}
                  onClick={() => handleTableClick(table)}
                >
                  {/* Table Name */}
                  <span className="font-bold text-lg leading-tight">{table.name}</span>
                  
                  {/* Capacity or Reserved/Cleaning indicator */}
                  {displayStatus === 'reserved' ? (
                    <span className="text-xs opacity-90 flex items-center gap-0.5">
                      <CalendarClock className="w-3 h-3" />
                    </span>
                  ) : displayStatus === 'cleaning' ? (
                    <span className="text-xs opacity-90 flex items-center gap-0.5">
                      <Sparkles className="w-3 h-3" />
                    </span>
                  ) : (
                    <span className="text-xs opacity-70 flex items-center gap-0.5">
                      <Users className="w-3 h-3" /> {table.capacity}
                    </span>
                  )}
                  
                  {/* Ticket Info */}
                  {table.ticket_summary && (
                    <div className="mt-1 text-center">
                      <span className="text-xs font-medium">
                        {table.ticket_summary.guest_count}p
                      </span>
                      <span className="text-xs opacity-70 block">
                        {formatElapsed(table.ticket_summary.elapsed_minutes)}
                      </span>
                    </div>
                  )}
                  
                  {/* Reserved time hint */}
                  {displayStatus === 'reserved' && table.reserved_time && (
                    <span className="text-xs opacity-80 mt-0.5">
                      {new Date(table.reserved_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  
                  {/* Total if ordering/payment */}
                  {table.ticket_summary?.total > 0 && (
                    <span className="text-xs font-semibold mt-0.5 flex items-center">
                      <DollarSign className="w-3 h-3" />
                      {table.ticket_summary.total.toFixed(0)}
                    </span>
                  )}
                </div>
              );
            })}
            
            {/* Empty state */}
            {filteredTables.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tables in this section</p>
                  {canEditLayout && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => {
                        setEditMode(true);
                        setShowCreateTable(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add First Table
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Create Table Dialog */}
      <TableDialog 
        open={showCreateTable}
        onClose={() => setShowCreateTable(false)}
        onSave={handleCreateTable}
        sections={sections}
        mode="create"
      />
      
      {/* Edit Table Dialog */}
      <TableDialog 
        open={!!editingTable}
        onClose={() => setEditingTable(null)}
        onSave={handleUpdateTable}
        onDelete={() => handleDeleteTable(editingTable?.id)}
        sections={sections}
        table={editingTable}
        mode="edit"
      />
      
      {/* Table Action Drawer */}
      <TableActionDrawer 
        open={showActionDrawer}
        onClose={() => {
          setShowActionDrawer(false);
          setSelectedTable(null);
        }}
        table={selectedTable}
        onSeatTable={handleSeatFromDrawer}
        onResumeOrder={handleResumeFromDrawer}
        onRefresh={fetchTables}
      />
    </div>
  );
}

// Table Create/Edit Dialog
function TableDialog({ open, onClose, onSave, onDelete, sections, table, mode }) {
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState(4);
  const [shape, setShape] = useState('rectangle');
  const [section, setSection] = useState(DEFAULT_SECTION);
  const [width, setWidth] = useState(80);
  const [height, setHeight] = useState(80);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (table && mode === 'edit') {
      setName(table.name || '');
      setCapacity(table.capacity || 4);
      setShape(table.shape || 'rectangle');
      setSection(table.section || DEFAULT_SECTION);
      setWidth(table.width || 80);
      setHeight(table.height || 80);
    } else if (mode === 'create') {
      setName('');
      setCapacity(4);
      setShape('rectangle');
      setSection(sections[0] || DEFAULT_SECTION);
      setWidth(80);
      setHeight(80);
    }
  }, [table, mode, sections]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Table name is required');
      return;
    }
    
    setLoading(true);
    await onSave({
      name: name.trim(),
      capacity,
      shape,
      section,
      width,
      height,
      position_x: table?.position_x || 50,
      position_y: table?.position_y || 50
    });
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add New Table' : `Edit Table ${table?.name}`}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {mode === 'create' 
              ? 'Create a new table for your floor plan'
              : 'Update table properties'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Name */}
          <div>
            <label className="text-sm text-muted-foreground">Table Name *</label>
            <Input 
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., T1, Table 1, Booth A"
              className="bg-muted border-border mt-1"
              data-testid="table-name-input"
            />
          </div>
          
          {/* Capacity */}
          <div>
            <label className="text-sm text-muted-foreground">Capacity</label>
            <Input 
              type="number"
              value={capacity}
              onChange={e => setCapacity(parseInt(e.target.value) || 1)}
              min={1}
              max={20}
              className="bg-muted border-border mt-1"
              data-testid="table-capacity-input"
            />
          </div>
          
          {/* Shape */}
          <div>
            <label className="text-sm text-muted-foreground">Shape</label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                variant={shape === 'rectangle' ? 'default' : 'outline'}
                onClick={() => setShape('rectangle')}
                className={`flex-1 h-12 ${shape === 'rectangle' ? 'bg-secondary' : ''}`}
                data-testid="shape-rectangle"
              >
                <Square className="w-5 h-5 mr-2" /> Rectangle
              </Button>
              <Button
                type="button"
                variant={shape === 'circle' ? 'default' : 'outline'}
                onClick={() => setShape('circle')}
                className={`flex-1 h-12 ${shape === 'circle' ? 'bg-secondary' : ''}`}
                data-testid="shape-circle"
              >
                <Circle className="w-5 h-5 mr-2" /> Circle
              </Button>
            </div>
          </div>
          
          {/* Section */}
          <div>
            <label className="text-sm text-muted-foreground">Section</label>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="bg-muted border-border mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sections.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
                <SelectItem value="__new__">+ New Section...</SelectItem>
              </SelectContent>
            </Select>
            {section === '__new__' && (
              <Input 
                placeholder="Enter new section name"
                className="bg-muted border-border mt-2"
                onChange={e => setSection(e.target.value)}
                autoFocus
              />
            )}
          </div>
          
          {/* Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Width (px)</label>
              <Input 
                type="number"
                value={width}
                onChange={e => setWidth(parseInt(e.target.value) || 80)}
                min={60}
                max={200}
                step={10}
                className="bg-muted border-border mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Height (px)</label>
              <Input 
                type="number"
                value={height}
                onChange={e => setHeight(parseInt(e.target.value) || 80)}
                min={60}
                max={200}
                step={10}
                className="bg-muted border-border mt-1"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          {mode === 'edit' && onDelete && (
            <Button 
              variant="destructive" 
              onClick={onDelete}
              className="mr-auto"
              data-testid="delete-table-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-secondary hover:bg-secondary/90"
            data-testid="save-table-btn"
          >
            {loading ? 'Saving...' : (mode === 'create' ? 'Create Table' : 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
